import { IRouteTree, IRoute, IRegion } from'../../../dip/library/di-hnf/di-hnf.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';

export type TViewRouteTable = Record<string, IRoute>;

/**
 * holds all routes in a table for fast lookup and configuring `Navigo` through the RouterTable
 */
export class ViewRouteTable {
  readonly lens = {
    layoutScheme: f1.lens('/layout_scheme'),
    styleRules: f1.lens('/style_rules')
  };

  read(routeTree: IRouteTree): TViewRouteTable {
    const stateTable = f1.pipe<IRouteTree, TViewRouteTable, TViewRouteTable, TViewRouteTable, TViewRouteTable>(
      [
        (routeTree) => this.__createViewStateTable(routeTree),
        (routeTable) => this.__calculateStyleControls(routeTable),
        (routeTable) => this.__calculateRegions(routeTable),
        (routeTable) => this.__calculateResourceURI(routeTable, routeTree)
      ],
      routeTree
    );

    return stateTable;
  }

  /**
   * calculate the full uri for each route and add to a list of all routes
   * @note we include routes which may not include regions (so they will not change the interface)
   */
  private __createViewStateTable(stateTree: IRouteTree): TViewRouteTable {
    const stateTable: TViewRouteTable = {};
    const includeRoute = (route: IRoute) => {
      route = this.__calculateFullFields(route);

      if (route.state.length > 0 && `${route.uri}`.length > 0) {
        stateTable[route.abs_state] = route;
      }

      return route;
    };

    f1.traverse(
      includeRoute,
      includeRoute,
      () => 'routes',
      (route: IRoute) => `${route.uri}`,
      stateTree
    );

    return stateTable;
  }

  /**
   *
   */
  private __calculateFullFields(route: IRoute): IRoute {
    const parents = route.parents || [];
    const fullState = [
      parents
        .map((v) => v.state)
        .filter(f1.identity)
        .join('.'),
      route.state
    ];

    route.uri = '' + route.uri;
    route.abs_uri = parents.map((v) => v.uri).join('') + route.uri;
    route.abs_state = fullState.filter(f1.identity).join('.');
    return route;
  }

  /**
   * for every node/leaf with a region, we get the layout_scheme
   */
  private __calculateStyleControls(stateTable: TViewRouteTable): TViewRouteTable {
    return f1.each((route: IRoute) => {
      const walkParents = f1.reverse(f1.safe([], route.parents));

      route.layout_scheme =
        route.layout_scheme ??
        f1.read(
          this.lens.layoutScheme,
          '',
          walkParents.find((r) => r.layout_scheme)
        );

      route.style_rules =
        route.style_rules ??
        f1.read(
          this.lens.styleRules,
          '',
          walkParents.find((r) => r.style_rules)
        );

      return route;
    }, stateTable);
  }

  /**
   * for every node/leaf with a region, we build the list of regions from all parents
   */
  private __calculateRegions(stateTable: TViewRouteTable): TViewRouteTable {
    return f1.each((route: IRoute) => {
      const regionDB: Record<string, IRegion> = {};

      f1.each((parentStateRoute: IRoute) => {
        f1.each((region: IRegion) => {
          regionDB[region.region] = regionDB[region.region] ?? region;
        }, parentStateRoute.regions);
      }, f1.concat(f1.reverse(f1.safe([], route.parents)), [route]));

      route.regions = f1.to.array(regionDB);
    }, stateTable);
  }

  /**
   * apply the source_uri to all view_uri or view statements
   */
  private __calculateResourceURI(stateTable: TViewRouteTable, routeTree: IRouteTree): TViewRouteTable {
    const topLevelResourceURI = routeTree.resource_uri;
    if (!topLevelResourceURI) {
      return stateTable;
    }

    return f1.each((route: IRoute) => {
      f1.each(
        (region: IRegion) => {
          region.view = `${topLevelResourceURI}${region.view}`;
        },
        f1.filter((region) => f1.is.exists(region.view), route.regions)
      );

      f1.each(
        (region: IRegion) => {
          region.view_uri = `${topLevelResourceURI}${region.view_uri}`;
        },
        f1.filter((region) => f1.is.exists(region.view_uri), route.regions)
      );
    }, stateTable);
  }
}
